/** Shared types for the AI training / exam-corpus surface. */

export type PaperStatus =
  | 'queued'
  | 'parsing'
  | 'chunking'
  | 'embedding'
  | 'extracting_outcomes'
  | 'ready'
  | 'failed';

export type PaperType = 'CAT' | 'Final' | 'Re-sit' | 'Mock' | 'Other';

export interface Paper {
  id: string;
  file_name: string;
  course_id?: string;
  course_code?: string;
  course_title?: string;
  academic_session?: string;
  year?: string;
  paper_type?: PaperType;
  /** Cascading curriculum path — filled at upload time by the taxonomy picker. */
  speciality?: string;
  level?: string;
  module?: string;
  unit?: string;
  page_count?: number;
  chunk_count?: number;
  question_count?: number;
  status: PaperStatus;
  error?: string;
  uploaded_at?: string;
  uploaded_by?: string;
  embedding_model?: string;
}

export interface CorpusStats {
  total_papers: number;
  ready: number;
  processing: number;
  failed: number;
  total_chunks: number;
  total_questions: number;
  last_upload?: string;
}

export interface CourseOption {
  id: string;
  code: string;
  title: string;
}

/**
 * A pending upload is either a local File OR a remote URL. The
 * backend accepts both — multipart form for files, JSON body for
 * URLs — but the picker + taxonomy metadata is identical either way.
 */
export type PendingSource = 'file' | 'url';

/** Discriminator between the two ingestion pipelines. `exam_paper`
 * (default) is the RAG past-paper corpus; `curriculum` triggers the
 * learning-outcome extractor and grounds the exam generator on the
 * institution's real outcomes. */
export type UploadKind = 'exam_paper' | 'curriculum';

export interface PendingUpload {
  id: string;
  source: PendingSource;
  /** Defaults to 'exam_paper'. Set per-item in the pending sheet. */
  kind?: UploadKind;
  /** Populated when source === 'file'. */
  file?: File;
  /** Populated when source === 'url'. */
  url?: string;
  /** Human-readable name shown in the sheet (either file.name or a hostname/path from the URL). */
  display_name: string;
  /** File size in bytes when known (file uploads; URLs are unknown until server fetches). */
  size_bytes?: number;
  /** Cascading curriculum path — set via the taxonomy picker. */
  speciality_key?: string;
  speciality_name?: string;
  level_key?: string;
  level_name?: string;
  module_key?: string;
  module_title?: string;
  course_id?: string;      // slug key from the picker (unique across tenants)
  course_code?: string;
  course_title?: string;
  year?: string;
  paper_type?: PaperType;
  academic_session?: string;
  progress: number;
  error?: string;
}

/* ── Taxonomy tree (from /v1/taxonomy) ─────────────────────────── */
export interface TaxonomyCourse {
  key:   string;
  code:  string;
  title: string;
}
export interface TaxonomyModule {
  key:     string;
  title:   string;
  courses: TaxonomyCourse[];
}
export interface TaxonomyLevel {
  key:          string;
  name:         string;
  level_number: number | null;
  modules:      TaxonomyModule[];
}
export interface TaxonomySpeciality {
  key:    string;
  name:   string;
  levels: TaxonomyLevel[];
}
export interface Taxonomy {
  specialities: TaxonomySpeciality[];
  generated_at?: string;
  counts?: {
    specialities: number;
    levels:       number;
    modules:      number;
    courses:      number;
  };
}

export const STATUS_META: Record<
  PaperStatus,
  { label: string; tw: string; dotTw: string }
> = {
  queued:    { label: 'Queued',       tw: 'bg-slate-100 text-slate-600',         dotTw: 'bg-slate-400' },
  parsing:   { label: 'Parsing PDF',  tw: 'bg-blue-50 text-[#2e86ab]',           dotTw: 'bg-[#2e86ab]' },
  chunking:  { label: 'Chunking',     tw: 'bg-blue-50 text-[#2e86ab]',           dotTw: 'bg-[#2e86ab]' },
  embedding: { label: 'Embedding',    tw: 'bg-purple-50 text-[#6c3483]',         dotTw: 'bg-[#6c3483]' },
  ready:     { label: 'Ready',        tw: 'bg-emerald-50 text-[#0e9172]',        dotTw: 'bg-[#0e9172]' },
  failed:    { label: 'Failed',       tw: 'bg-red-50 text-[#c0392b]',            dotTw: 'bg-[#c0392b]' },
  // Curriculum-kind extra step — runs after embedding when the
  // pipeline parses the CDACC learning-outcome tree out of the PDF.
  extracting_outcomes: { label: 'Extracting outcomes', tw: 'bg-amber-50 text-[#b7860b]', dotTw: 'bg-[#b7860b]' },
};

// Safe fallback so any future backend-side status never crashes the
// card render. Consumers should call getStatusMeta(status) rather than
// dereferencing STATUS_META[status] directly.
export const getStatusMeta = (status?: string) =>
  (status && (STATUS_META as any)[status])
    || { label: status || 'Unknown', tw: 'bg-slate-100 text-slate-600', dotTw: 'bg-slate-400' };

export const formatRelative = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
